import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
} from '@mui/material';
import { jsonFieldError } from 'common/packages';

const fromPackage = (pkg) => ({
  packageName: pkg.name || '',
  version: pkg.version || '',
  type: pkg.type || '',
  fileName: pkg.file_name || '',
  label: pkg.label || '',
  description: pkg.description || '',
  compatibility: pkg.compatibility || '',
  manifest: pkg.manifest || '',
  signature: pkg.signature || '',
  url: pkg.url || '',
  isPublic: Boolean(pkg.is_public),
  isVisible: Boolean(pkg.is_visible),
});

const MetadataForm = (props) => {
  const { pkg, readOnly, saving, fieldErrors, onSave, onCancel } = props;

  const [values, setValues] = useState(() => fromPackage(pkg));
  const [blurred, setBlurred] = useState({});

  // Re-seed whenever the package changes underneath the form — after a save,
  // a publish, or a re-upload.
  useEffect(() => {
    setValues(fromPackage(pkg));
    setBlurred({});
  }, [pkg]);

  const set = (field) => (event) =>
    setValues((previous) => ({ ...previous, [field]: event.target.value }));

  const toggle = (field) => (event) =>
    setValues((previous) => ({ ...previous, [field]: event.target.checked }));

  const compatibilityError =
    jsonFieldError(values.compatibility, true) || fieldErrors.compatibility || '';
  const manifestError = jsonFieldError(values.manifest, false) || fieldErrors.manifest || '';

  const original = fromPackage(pkg);
  const dirty = Object.keys(original).some((key) => original[key] !== values[key]);
  const required = values.packageName.trim() && values.version.trim() && values.type.trim();
  const canSave =
    !readOnly && dirty && Boolean(required) && !compatibilityError && !manifestError && !saving;

  const renaming =
    values.fileName !== original.fileName || values.type !== original.type;

  const text = (field, label, extra) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={values[field]}
      onChange={set(field)}
      disabled={readOnly}
      {...extra}
    />
  );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) onSave(values);
      }}
    >
      <Card>
        <CardHeader
          title="Metadata"
          subheader={
            readOnly
              ? 'Read-only. Use Edit above to change any of this.'
              : 'Every field is editable in any state. Identity is the UUID, which never changes.'
          }
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            {!readOnly && pkg.is_published && (
              <Alert severity="warning">
                This package is published. Saving keeps it published and the change
                goes live immediately.
              </Alert>
            )}
            {!readOnly && renaming && (
              <Alert severity="info">
                Changing the file name or type moves where the artifact is expected to
                live. Saving will ask what should happen to the stored objects.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {text('packageName', 'Package name', { required: true })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {text('version', 'Version', { required: true })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {text('type', 'Type', { required: true })}
              </Grid>

              <Grid item xs={12} sm={6}>
                {text('fileName', 'File name')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {text('label', 'Label')}
              </Grid>

              <Grid item xs={12} sm={6}>
                {text('signature', 'Signature')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {text('url', 'URL')}
              </Grid>

              <Grid item xs={12}>
                {text('description', 'Description', { multiline: true, minRows: 2 })}
              </Grid>

              <Grid item xs={12} sm={6}>
                {text('compatibility', 'Compatibility', {
                  multiline: true,
                  minRows: 4,
                  placeholder: '["version expression","semver expression"]',
                  onBlur: () => setBlurred((p) => ({ ...p, compatibility: true })),
                  error: Boolean((blurred.compatibility || fieldErrors.compatibility) && compatibilityError),
                  helperText:
                    ((blurred.compatibility || fieldErrors.compatibility) && compatibilityError) || ' ',
                  InputProps: { sx: { fontFamily: 'monospace' } },
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {text('manifest', 'Manifest', {
                  multiline: true,
                  minRows: 4,
                  onBlur: () => setBlurred((p) => ({ ...p, manifest: true })),
                  error: Boolean((blurred.manifest || fieldErrors.manifest) && manifestError),
                  helperText: ((blurred.manifest || fieldErrors.manifest) && manifestError) || ' ',
                  InputProps: { sx: { fontFamily: 'monospace' } },
                })}
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={values.isPublic}
                        onChange={toggle('isPublic')}
                        disabled={readOnly}
                      />
                    }
                    label="Public — anyone can download it, anonymous callers included. Unchecked: only accounts on the access list, and only if they are licensed for this application."
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isVisible}
                        onChange={toggle('isVisible')}
                        disabled={readOnly}
                      />
                    }
                    label="Visible"
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
        {!readOnly && (
          <>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setValues(fromPackage(pkg));
                  setBlurred({});
                  onCancel();
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disableElevation disabled={!canSave}>
                Save changes
              </Button>
            </CardActions>
          </>
        )}
      </Card>
    </form>
  );
};

MetadataForm.propTypes = {
  fieldErrors: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  pkg: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  saving: PropTypes.bool,
};

MetadataForm.defaultProps = {
  fieldErrors: {},
};

export default MetadataForm;
